import {
  ConfirmationModal,
  PrimaryButton,
  Text,
  Checkbox,
  IconButton,
  DeleteIcon,
  SecondaryButton,
  Heading,
  CheckIcon,
  XIcon,
  HelpIcon,
  MinusIcon,
} from '@weave/design-system';
import { theme } from '@weave/theme';
import { useDeleteEntityTagMutation } from 'apis/wetags';
import { useWipeTagMutation } from 'apis/wetags/mutations';
import {
  getTagStatsQuery,
  listEntityTagsQuery,
  useInfiniteListEntityTagsQuery,
} from 'apis/wetags/queriesV2';
import React, { useCallback, useMemo, useState } from 'react';
import { useModalControl } from '@weave/design-system';
import { List, ListItem } from '../components/List.component';
import { useAlert } from '@weave/alert-system';
import { Link } from 'react-router-dom';
import { DetailRow } from './DetailRow';
import { CheckEntity } from './CheckEntity.component';

type AllowDenyTagManagerProps = {
  namespaceId: string;
  allowTagId: string;
  denyTagId?: string;
  canCreateDenyList?: boolean;
  allowPercentage?: number;
  allowExplicitlyTaggedCount?: number;
  denyPercentage?: number;
  denyExplicitlyTaggedCount?: number;
  isEnabled: boolean;
  onEnable: () => void | Promise<void>;
  onDisable: () => void | Promise<void>;
  onAllowAll: () => void | Promise<void>;
  onCreateDenyList: () => void | Promise<void>;
  onClickSetAllowPercentage: () => void;
  onClickSetDenyPercentage: () => void;
  onClickAddAllow: () => void;
  onClickAddDeny: () => void;
};

type ResolvedStateTone = 'success' | 'warning' | 'critical' | 'neutral';

type ResolvedState = {
  label: string;
  description: string;
  tone: ResolvedStateTone;
};

function getResolvedState({
  allowPercentage,
  allowExplicitlyTaggedCount,
  denyPercentage,
  denyExplicitlyTaggedCount,
}: {
  allowPercentage?: number;
  allowExplicitlyTaggedCount?: number;
  denyPercentage?: number;
  denyExplicitlyTaggedCount?: number;
}): ResolvedState {
  const allowPct = allowPercentage ?? 0;
  const denyPct = denyPercentage ?? 0;
  const allowExplicitCount = allowExplicitlyTaggedCount ?? 0;
  const denyExplicitCount = denyExplicitlyTaggedCount ?? 0;

  if (denyPct === 100) {
    return {
      label: 'Fully Blocked',
      description:
        'Deny rollout is 100%, so all entities are denied. Deny rules override allow rules.',
      tone: 'critical',
    };
  }

  if (
    allowPct === 0 &&
    denyPct === 0 &&
    allowExplicitCount === 0 &&
    denyExplicitCount === 0
  ) {
    return {
      label: 'Inactive',
      description: 'Both rollouts are 0% and there are no explicit allow/deny entities.',
      tone: 'neutral',
    };
  }

  if (allowExplicitCount > 0 && denyExplicitCount > 0) {
    return {
      label: 'Mixed (Deny Priority)',
      description:
        'Both explicit allow and deny entities exist. If an entity is in both lists, deny wins.',
      tone: 'warning',
    };
  }

  if (denyExplicitCount > 0) {
    return {
      label: 'Partially Blocked',
      description:
        'Some entities are explicitly denied. Explicit deny entries always apply and override allow rules.',
      tone: 'warning',
    };
  }

  if (allowPct > 0 && denyPct > 0) {
    return {
      label: 'Mixed Rollout (Deny Priority)',
      description:
        'Both allow and deny rollouts are active. Deny rollout takes precedence for overlapping entities.',
      tone: 'warning',
    };
  }

  if (allowExplicitCount > 0 && allowPct === 0) {
    return {
      label: 'Explicit Allow Only',
      description:
        'Allow rollout is 0%, but explicitly allowed entities are active unless denied by deny rules.',
      tone: 'success',
    };
  }

  if (allowPct > 0 && denyPct === 0 && denyExplicitCount === 0) {
    return {
      label: 'Enabled (Allow Rollout)',
      description: 'Allow rollout is active with no deny rules.',
      tone: 'success',
    };
  }

  if (allowPct > 0 && denyPct === 0) {
    return {
      label: 'Enabled (Allow Rollout)',
      description:
        'Allow rollout is active with no deny rollout. Explicit deny entities still override when present.',
      tone: 'success',
    };
  }

  return {
    label: 'Enabled',
    description: 'Rules are active with deny taking precedence over allow.',
    tone: 'success',
  };
}

export function AllowDenyTagManager({
  namespaceId,
  allowTagId,
  denyTagId,
  canCreateDenyList = true,
  allowPercentage,
  allowExplicitlyTaggedCount,
  denyPercentage,
  denyExplicitlyTaggedCount,
  isEnabled,
  onEnable,
  onDisable,
  onAllowAll,
  onCreateDenyList,
  onClickSetAllowPercentage,
  onClickSetDenyPercentage,
  onClickAddAllow,
  onClickAddDeny,
}: AllowDenyTagManagerProps) {
  const alerts = useAlert();
  const { mutateAsync: deleteEntityTagAsync, isLoading: isDeletingEntityTag } =
    useDeleteEntityTagMutation();
  const { modalProps: allowAllModalProps, triggerProps: allowAllTriggerProps } =
    useModalControl();

  const { modalProps, triggerProps } = useModalControl();
  const [deletionTarget, setDeletionTarget] = useState<{
    tagId: string;
    entityIds: string[];
    isDenyList: boolean;
  } | null>(null);
  const deletionSuccessCallbackRef = React.useRef<
    ((deletedEntityIds: string[]) => void) | null
  >(null);

  const onOpenDelete = useCallback(
    (
      tagId: string,
      entityIds: string[],
      isDenyList: boolean,
      onDeleted?: (deletedEntityIds: string[]) => void
    ) => {
      setDeletionTarget({ tagId, entityIds, isDenyList });
      deletionSuccessCallbackRef.current = onDeleted ?? null;
      triggerProps.onClick();
    },
    [triggerProps]
  );

  const onDeleteEntities = useCallback(async () => {
    if (!deletionTarget || deletionTarget.entityIds.length === 0) {
      return;
    }

    try {
      const results = await Promise.allSettled(
        deletionTarget.entityIds.map((entityId) =>
          deleteEntityTagAsync({
            namespaceId,
            tagId: deletionTarget.tagId,
            entityId,
          })
        )
      );

      const succeededEntityIds = deletionTarget.entityIds.filter(
        (_, index) => results[index].status === 'fulfilled'
      );
      const failedCount = results.length - succeededEntityIds.length;

      if (succeededEntityIds.length > 0) {
        getTagStatsQuery.invalidate({
          namespaceId,
          tagId: deletionTarget.tagId,
        });
        listEntityTagsQuery.invalidate({
          namespaceId,
          tagId: deletionTarget.tagId,
        });
        deletionSuccessCallbackRef.current?.(succeededEntityIds);
      }

      if (failedCount > 0) {
        alerts.error(
          succeededEntityIds.length > 0
            ? 'Some entity tags could not be deleted. Please try again.'
            : 'Failed to delete entity tags. Please try again.'
        );
      }
    } catch (error) {
      alerts.error('Failed to delete entity tags. Please try again.');
    }
  }, [alerts, deleteEntityTagAsync, deletionTarget, namespaceId]);

  const resolvedState = useMemo(
    () =>
      getResolvedState({
        allowPercentage,
        allowExplicitlyTaggedCount,
        denyPercentage,
        denyExplicitlyTaggedCount,
      }),
    [
      allowExplicitlyTaggedCount,
      allowPercentage,
      denyExplicitlyTaggedCount,
      denyPercentage,
    ]
  );
  const shouldConfirmAllowAll = (denyExplicitlyTaggedCount ?? 0) > 0;

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: theme.spacing(1),
          borderRadius: theme.borderRadius.small,
          background: theme.colors.neutral5,
          height: 60,
        }}
      >
        <Heading level={3} css={{ margin: 0 }}>
          Entity Rules
        </Heading>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: theme.spacing(1),
        }}
      >
        <Text size="large" weight="bold" css={{ margin: 0 }}>
          Quick Actions
        </Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          <SecondaryButton
            style={{ width: 'auto', maxWidth: 'max-content' }}
            disabled={isEnabled}
            onClick={onEnable}
          >
            Clear Block Override
          </SecondaryButton>
          <Text size="small" css={{ margin: 0 }}>
            Remove the global deny override by setting deny percentage to 0%.
          </Text>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          <SecondaryButton
            style={{ width: 'auto', maxWidth: 'max-content' }}
            disabled={!isEnabled}
            onClick={onDisable}
          >
            Block All
          </SecondaryButton>
          <Text size="small" css={{ margin: 0 }}>
            Apply a global deny override by setting deny percentage to 100%.
          </Text>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          <SecondaryButton
            style={{ width: 'auto', maxWidth: 'max-content' }}
            onClick={shouldConfirmAllowAll ? allowAllTriggerProps.onClick : onAllowAll}
          >
            Allow All
          </SecondaryButton>
          <Text size="small" css={{ margin: 0 }}>
            Allow all entities. Equivalent to setting allow percentage to 100%, deny
            percentage to 0% and removing all explicitly denied entities.
          </Text>
        </div>
      </div>

      <Text size="large" weight="bold" css={{ margin: 0 }}>
        Resolved State
      </Text>
      <div
        style={{
          border: `1px solid ${theme.colors.neutral10}`,
          borderRadius: theme.borderRadius.small,
          padding: theme.spacing(2),
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(0.5),
        }}
      >
        <ResolvedStateLabel label={resolvedState.label} tone={resolvedState.tone} />
        <Text size="medium" css={{ margin: 0 }}>
          {resolvedState.description}
        </Text>
      </div>

      <Text size="large" weight="bold" css={{ margin: 0 }}>
        Advanced Rules Management
      </Text>
      <DetailRow
        label="Overriding Tag"
        value={
          <Link to={`/wetags/namespace/${namespaceId}/tag/${denyTagId}`}>
            {denyTagId}
          </Link>
        }
      />
      <div style={{ position: 'relative', marginTop: theme.spacing(1) }}>
        <section
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: theme.spacing(2),
          }}
        >
          <EntityListCard
            title="Allow List"
            namespaceId={namespaceId}
            tagId={allowTagId}
            percentage={allowPercentage}
            onClickSetPercentage={onClickSetAllowPercentage}
            disableSetPercentage={false}
            onClickAdd={onClickAddAllow}
            onClickDelete={(entityIds, onDeleted) =>
              onOpenDelete(allowTagId, entityIds, false, onDeleted)
            }
            disableDelete={isDeletingEntityTag}
            emptyState="No entities in allow list."
          />

          {denyTagId ? (
            <EntityListCard
              title="Deny List"
              namespaceId={namespaceId}
              tagId={denyTagId}
              percentage={denyPercentage}
              onClickSetPercentage={onClickSetDenyPercentage}
              disableSetPercentage={!denyTagId}
              onClickAdd={onClickAddDeny}
              onClickDelete={(entityIds, onDeleted) => {
                onOpenDelete(denyTagId, entityIds, true, onDeleted);
              }}
              disableDelete={isDeletingEntityTag}
              emptyState="No entities in deny list."
            />
          ) : canCreateDenyList ? (
            <div
              style={{
                border: `1px solid ${theme.colors.neutral10}`,
                borderRadius: theme.borderRadius.small,
                padding: theme.spacing(2),
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: theme.spacing(1),
              }}
            >
              <Text size="medium" weight="bold" css={{ margin: 0 }}>
                Deny List
              </Text>
              <Text size="medium" css={{ margin: 0 }}>
                No deny list exists for this tag.
              </Text>
              <PrimaryButton
                type="button"
                style={{ width: 'max-content' }}
                onClick={onCreateDenyList}
              >
                Create Deny List
              </PrimaryButton>
            </div>
          ) : (
            <div
              style={{
                border: `1px solid ${theme.colors.neutral10}`,
                borderRadius: theme.borderRadius.small,
                padding: theme.spacing(2),
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: theme.spacing(1),
              }}
            >
              <Text size="medium" weight="bold" css={{ margin: 0 }}>
                Deny List
              </Text>
              <Text size="medium" css={{ margin: 0 }}>
                Deny list is not available for this tag.
              </Text>
            </div>
          )}
        </section>
      </div>
      <ConfirmationModal
        {...modalProps}
        destructive
        title={
          deletionTarget && deletionTarget.entityIds.length > 1
            ? 'Confirm Bulk Delete'
            : 'Confirm Delete Entity'
        }
        onConfirm={onDeleteEntities}
      >
        {deletionTarget && (
          <Text size="medium" css={{ margin: theme.spacing(2, 0, 5) }}>
            {deletionTarget.entityIds.length > 1 ? (
              <React.Fragment>
                Are you sure you want to delete{' '}
                <Text as="span" size="medium" weight="bold">
                  {deletionTarget.entityIds.length} entities
                </Text>{' '}
                from the {deletionTarget.isDenyList ? 'deny' : 'allow'} list? This action
                cannot be undone.
              </React.Fragment>
            ) : (
              <React.Fragment>
                Are you sure you want to delete{' '}
                <Text as="span" size="medium" weight="bold">
                  {deletionTarget.entityIds[0]}
                </Text>{' '}
                from the {deletionTarget.isDenyList ? 'deny' : 'allow'} list? This action
                cannot be undone.
              </React.Fragment>
            )}
          </Text>
        )}
      </ConfirmationModal>

      <ConfirmationModal
        {...allowAllModalProps}
        title="Confirm Allow All"
        onConfirm={onAllowAll}
      >
        <Text size="medium" css={{ margin: theme.spacing(2, 0, 5) }}>
          This will set deny list percentage to 0% and remove all explicitly denied
          entities. Are you sure you want to continue?
        </Text>
      </ConfirmationModal>
    </>
  );
}

const TONE = {
  success: {
    icon: <CheckIcon size={16} />,
    background: theme.colors.success40,
    color: theme.colors.white,
  },
  warning: {
    icon: <HelpIcon size={16} />,
    background: theme.colors.warning50,
    color: theme.colors.black,
  },
  critical: {
    icon: <MinusIcon size={16} />,
    background: theme.colors.neutral40,
    color: theme.colors.white,
  },
  neutral: {
    icon: <HelpIcon size={16} />,
    background: theme.colors.neutral30,
    color: theme.colors.white,
  },
} as const;

const ResolvedStateLabel = ({
  label,
  tone,
}: {
  label: string;
  tone: 'success' | 'warning' | 'critical' | 'neutral';
}) => {
  const toneMeta = TONE[tone];

  return (
    <ReasonLabel
      icon={toneMeta.icon}
      text={label}
      background={toneMeta.background}
      color={toneMeta.color}
    />
  );
};

const ReasonLabel = ({
  icon,
  text,
  color,
  background,
}: {
  icon: React.ReactNode;
  text: string;
  color?: string;
  background?: string;
}) => {
  return (
    <span
      style={{
        borderRadius: theme.borderRadius.small,
        padding: theme.spacing(0.5, 1),
        background: background || color || theme.colors.neutral20,
        width: 'fit-content',
        color: color || theme.colors.black,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      {icon}{' '}
      <Text size="medium" as="span" style={{ color }}>
        {text}
      </Text>
    </span>
  );
};

function EntityListCard({
  title,
  namespaceId,
  tagId,
  percentage,
  onClickSetPercentage,
  disableSetPercentage,
  onClickAdd,
  onClickDelete,
  disableDelete,
  emptyState,
}: {
  title: string;
  namespaceId: string;
  tagId: string;
  percentage?: number;
  onClickSetPercentage: () => void;
  disableSetPercentage?: boolean;
  onClickAdd: () => void;
  onClickDelete: (
    entityIds: string[],
    onDeleted?: (deletedEntityIds: string[]) => void
  ) => void;
  disableDelete?: boolean;
  emptyState: string;
}) {
  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);
  const alerts = useAlert();
  const { mutateAsync: wipeTagAsync, isLoading: isWipingTag } = useWipeTagMutation();
  const { modalProps: wipeModalProps, triggerProps: wipeTriggerProps } =
    useModalControl();
  const {
    data: entityTagsPages,
    isLoading,
    isFetchingNextPage: isFetchingMore,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteListEntityTagsQuery({
    namespaceId,
    tagId,
    pageSize: 50,
  });
  const entities = useMemo(
    () =>
      entityTagsPages?.pages
        .flatMap((page) => page.entityTags ?? [])
        .map((entityTag) => entityTag.entityId ?? '') ?? [],
    [entityTagsPages]
  );

  const onToggleEntitySelection = useCallback(
    (entityId: string) => {
      setSelectedEntityIds((currentValue) =>
        currentValue.includes(entityId)
          ? currentValue.filter((currentEntityId) => currentEntityId !== entityId)
          : [...currentValue, entityId]
      );
    },
    [setSelectedEntityIds]
  );

  const onWipeAllEntities = useCallback(async () => {
    try {
      await wipeTagAsync({ namespaceId, tagId });
      getTagStatsQuery.invalidate({ namespaceId, tagId });
      listEntityTagsQuery.invalidate({ namespaceId, tagId });
      setSelectedEntityIds([]);
    } catch (error) {
      alerts.error('Failed to remove all explicitly tagged entities. Please try again.');
    }
  }, [alerts, namespaceId, tagId, wipeTagAsync]);

  return (
    <div
      style={{
        border: `1px solid ${theme.colors.neutral10}`,
        borderRadius: theme.borderRadius.small,
        padding: theme.spacing(2),
        display: 'grid',
        gridTemplateRows: 'auto auto auto 1fr',
        gap: theme.spacing(2),
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: theme.spacing(1),
          paddingBottom: theme.spacing(2),
          borderBottom: `1px solid ${theme.colors.neutral10}`,
        }}
      >
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}
        >
          <Text size="medium" weight="bold" css={{ margin: 0 }}>
            {title}
          </Text>
          <Text size="medium" css={{ margin: 0 }}>
            Percentage: {percentage ?? 0}%
          </Text>
        </div>
        <SecondaryButton
          type="button"
          style={{ width: 'max-content' }}
          disabled={disableSetPercentage}
          onClick={onClickSetPercentage}
        >
          Set Percentage
        </SecondaryButton>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(1),
          paddingBottom: theme.spacing(2),
          borderBottom: `1px solid ${theme.colors.neutral10}`,
        }}
      >
        <Text size="medium" weight="bold" css={{ margin: 0 }}>
          Check Entity
        </Text>
        <CheckEntity
          namespaceId={namespaceId}
          tagId={tagId}
          isDeletingEntity={disableDelete}
          currentPercentage={percentage}
          onDeleteEntity={(entityId, onDeleted) => {
            onClickDelete([entityId], () => {
              setSelectedEntityIds((currentValue) =>
                currentValue.filter((id) => id !== entityId)
              );
              return onDeleted?.();
            });
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(1),
          paddingBottom: theme.spacing(2),
          borderBottom: `1px solid ${theme.colors.neutral10}`,
        }}
      >
        <Text size="medium" weight="bold" css={{ margin: 0 }}>
          Tagged Entities
        </Text>
        <section
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'end',
          }}
        >
          <Text size="medium" css={{ margin: 0 }}>
            {selectedEntityIds.length} selected
          </Text>

          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
            <PrimaryButton
              type="button"
              style={{ width: 'max-content' }}
              onClick={onClickAdd}
            >
              Add
            </PrimaryButton>
            <PrimaryButton
              type="button"
              destructive
              style={{ width: 'max-content' }}
              disabled={isLoading || entities.length === 0 || isWipingTag}
              onClick={wipeTriggerProps.onClick}
            >
              Remove All
            </PrimaryButton>
            <PrimaryButton
              type="button"
              destructive
              disabled={selectedEntityIds.length === 0 || !!disableDelete}
              style={{ width: 'max-content' }}
              onClick={() => {
                onClickDelete(selectedEntityIds, (deletedEntityIds) => {
                  setSelectedEntityIds((currentValue) =>
                    currentValue.filter((id) => !deletedEntityIds.includes(id))
                  );
                });
              }}
            >
              Delete selected
            </PrimaryButton>
          </div>
        </section>
      </div>

      <List
        width={'100%'}
        canSearch={entities.length > 10}
        onReachEnd={fetchNextPage}
        hasMore={!!hasNextPage}
        isFetchingMore={isFetchingMore}
        list={entities.map((entityId) => ({
          id: entityId,
          label: entityId,
        }))}
        renderItem={(item, options) => (
          <ListItem key={item.id} {...options.listItemProps}>
            <div
              {...options.focusableProps}
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '1rem',
                ':focus': {
                  outline: 'none',
                },
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing(1),
                  minWidth: 0,
                }}
              >
                <Checkbox
                  value={selectedEntityIds.includes(item.id)}
                  onChange={() => {
                    onToggleEntitySelection(item.id);
                  }}
                  id={''}
                  aria-invalid={false}
                  onFocus={() => {}}
                  onBlur={() => {}}
                  active={false}
                  error={false}
                  name={''}
                />
                <Text
                  size="medium"
                  css={{ margin: 0, fontFamily: 'monospace !important' }}
                >
                  {item.label}
                </Text>
              </div>
              <IconButton
                label="Delete Entity"
                name="deleteEntity"
                destructive
                onClick={() => {
                  onClickDelete([item.id], (deletedEntityIds) => {
                    setSelectedEntityIds((currentValue) =>
                      currentValue.filter((id) => !deletedEntityIds.includes(id))
                    );
                  });
                }}
              >
                <DeleteIcon />
              </IconButton>
            </div>
          </ListItem>
        )}
      />
      {isFetchingMore && (
        <Text size="medium" css={{ margin: theme.spacing(2, 1, 0) }}>
          Loading more...
        </Text>
      )}

      <ConfirmationModal
        {...wipeModalProps}
        destructive
        title="Confirm Remove All Explicitly Tagged Entities"
        onConfirm={onWipeAllEntities}
      >
        <Text size="medium" css={{ margin: theme.spacing(2, 0, 5) }}>
          Are you sure you want to remove all explicitly tagged entities from this list?
          This action cannot be undone.
        </Text>
      </ConfirmationModal>
    </div>
  );
}
